import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, TrendingUp } from 'lucide-react';
import { useUploads } from '@/api/hooks/useBloodwork';
import { useTrends } from '@/api/hooks/useTrends';
import { BiomarkerSelector } from '@/components/history/BiomarkerSelector';
import { TrendChart } from '@/components/history/TrendChart';
import { UploadTimeline } from '@/components/history/UploadTimeline';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

const stagger = {
  animate: { transition: { staggerChildren: 0.08 } },
};

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' as const } },
};

export function HistoryPage() {
  const [selectedBiomarker, setSelectedBiomarker] = useState<string | null>(null);

  const { data: uploadsData, isLoading: uploadsLoading } = useUploads();
  const { data: trendsData, isLoading: trendsLoading } = useTrends(selectedBiomarker);

  // Parse available biomarkers from the overview trends data (when no biomarker selected)
  const { data: overviewData } = useTrends(null);

  const availableBiomarkers = useMemo(() => {
    if (!overviewData?.trends) return [];
    const seen = new Map<string, { key: string; name: string; category: string }>();

    for (const point of overviewData.trends) {
      if (point.markers) {
        for (const m of point.markers) {
          if (!seen.has(m.key)) {
            seen.set(m.key, {
              key: m.key,
              name: m.name,
              category: m.category || 'other',
            });
          }
        }
      }
    }

    return Array.from(seen.values());
  }, [overviewData]);

  // Find optimal ranges for the selected biomarker from the overview data
  const selectedBiomarkerInfo = useMemo(() => {
    if (!selectedBiomarker || !overviewData?.trends) return null;

    for (const point of overviewData.trends) {
      if (point.markers) {
        const m = point.markers.find((mk: any) => mk.key === selectedBiomarker);
        if (m) {
          return {
            name: m.name,
            unit: m.unit,
            optimalLow: (m as any).optimal_low,
            optimalHigh: (m as any).optimal_high,
          };
        }
      }
    }
    return null;
  }, [selectedBiomarker, overviewData]);

  // Map uploads to the timeline format
  const timelineUploads = useMemo(() => {
    if (!uploadsData?.uploads) return [];
    return uploadsData.uploads.map((u: any) => ({
      id: u.upload_id || u.id,
      lab_date: u.lab_date,
      lab_provider: u.lab_provider || null,
      status: u.upload_status || u.status,
      tier: u.tier || null,
      summary: u.summary || null,
    }));
  }, [uploadsData]);

  // Empty state
  if (!uploadsLoading && (!uploadsData?.uploads || uploadsData.uploads.length === 0)) {
    return (
      <div className="p-6 lg:p-8 max-w-6xl mx-auto">
        <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-copper/10 mb-4">
            <TrendingUp className="h-8 w-8 text-copper" />
          </div>
          <h1 className="text-xl font-bold text-navy">
            Upload your first lab results to start tracking trends
          </h1>
          <p className="mt-2 text-sm text-gray-500 max-w-md">
            Once you have uploaded lab results, this page will show your biomarker trends
            over time and a complete history of all your analyses.
          </p>
          <Link to="/upload" className="mt-6">
            <Button>
              <Upload className="mr-2 h-4 w-4" />
              Upload Labs
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <motion.div variants={stagger} initial="initial" animate="animate">
        {/* Page header */}
        <motion.div variants={fadeUp} className="mb-8">
          <h1 className="text-2xl font-bold text-navy">History</h1>
          <p className="mt-1 text-gray-500">
            Track your biomarker trends over time
          </p>
        </motion.div>

        {/* Biomarker selector pills */}
        <motion.section variants={fadeUp} className="mb-6">
          {uploadsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-7 w-20 rounded-full" />
                ))}
              </div>
            </div>
          ) : (
            <BiomarkerSelector
              availableBiomarkers={availableBiomarkers}
              selected={selectedBiomarker}
              onSelect={setSelectedBiomarker}
            />
          )}
        </motion.section>

        {/* Trend chart */}
        <motion.section variants={fadeUp} className="mb-8">
          {trendsLoading ? (
            <Skeleton className="h-[360px] w-full rounded-xl" />
          ) : selectedBiomarker && trendsData?.trends ? (
            <TrendChart
              data={trendsData.trends}
              biomarkerName={selectedBiomarkerInfo?.name || selectedBiomarker}
              optimalLow={selectedBiomarkerInfo?.optimalLow}
              optimalHigh={selectedBiomarkerInfo?.optimalHigh}
              unit={selectedBiomarkerInfo?.unit}
            />
          ) : !selectedBiomarker ? (
            <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
              <TrendingUp className="h-8 w-8 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-500">
                Select a biomarker above to view its trend over time
              </p>
            </div>
          ) : null}
        </motion.section>

        {/* Upload timeline */}
        <motion.section variants={fadeUp}>
          {uploadsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full rounded-xl" />
              ))}
            </div>
          ) : (
            <UploadTimeline uploads={timelineUploads} />
          )}
        </motion.section>
      </motion.div>
    </div>
  );
}
